import { create } from "zustand";
import {
  budgetFor,
  styleFor,
  transitionKindFor,
  transitionPlan,
  transitionsDisabled,
  REDUCED_MOTION_MS,
  TRANSITION_WATCHDOG_SLACK_MS,
  type TransitionPlan,
  type TransitionRequest,
} from "./timing";
import { primeCardImages } from "./cardImages";
import { slideFor, type SlideDirection } from "./slide";

/**
 * The screen-transition state machine, as a module store rather than React
 * state — the same shape `state/notifications.ts` uses, and for the same reason:
 * it is driven from callbacks all over the app and read by exactly one component
 * at the root.
 *
 * Two invariants, and neither is a nicety. This app has been wedged before by an
 * animation flag that did not come back down (see CLAUDE.md on `animatingRef`
 * and `restImpacts`), and the failure here is worse: a transition overlay that
 * sticks is a blank app with no way out but a reload.
 *
 *  1. **The navigation happens on a timer, not on an animation callback.** The
 *     cards are decoration over a `setTimeout` that was scheduled the moment the
 *     player tapped. If nothing renders, if the Web Animations API is missing,
 *     if the overlay throws — the app still arrives where it was going.
 *  2. **A watchdog clears the overlay unconditionally.** It is armed for twice
 *     the budget plus a slack, so it cannot fire during a healthy run and cannot
 *     fail to fire during a sick one.
 *
 * The third rule is about the player pressing things twice: at most one run is
 * ever in flight, and **the newest destination wins**. A second request during
 * the cover simply re-points the pending navigation; one after the swap gives up
 * on the animation and navigates immediately. A transition may look wrong under
 * a double tap; it may never eat one.
 */

export type TransitionPhase = "covering" | "revealing";

export interface TransitionRun {
  /** Distinguishes consecutive runs, so the overlay rebuilds rather than resuming. */
  id: number;
  plan: TransitionPlan;
  /** Layout seed, stable for the run so a resize re-lays the same pack. */
  seed: number;
  back: boolean;
  /** Which way the screens travel. Only the slide reads it. */
  direction: SlideDirection;
}

interface TransitionState {
  run: TransitionRun | null;
  phase: TransitionPhase;
}

export const useTransition = create<TransitionState>(() => ({ run: null, phase: "covering" }));

/**
 * The react-router `navigate`, as handed in by whoever is driving.
 *
 * Typed structurally rather than imported from the router: this module is a
 * timer and a phase, and nothing in it should need to know which router the app
 * is using. `NavOptions` is whatever the caller was going to pass anyway — it is
 * carried through untouched, so `replace` and route `state` reach the router
 * exactly as they would have without a transition in the way.
 */
export interface NavOptions {
  replace?: boolean;
  state?: unknown;
}
type Navigate = (to: string, opts?: NavOptions) => void;

let nextId = 1;
let timers: number[] = [];
let watchdog: number | null = null;
/** Where the run in flight is headed, and whether it has got there yet. */
let pending: { to: string; navigate: Navigate; opts: NavOptions; swapped: boolean } | null = null;

function clearTimers(): void {
  for (const t of timers) window.clearTimeout(t);
  timers = [];
  if (watchdog !== null) window.clearTimeout(watchdog);
  watchdog = null;
}

/** Puts the overlay away and forgets the run. Safe to call at any point. */
function finish(): void {
  clearTimers();
  pending = null;
  useTransition.setState({ run: null, phase: "covering" });
}

function reducedMotion(): boolean {
  return typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Whether the Web Animations API is available to drive the cards.
 *
 * Without it the cards would be mounted in their landed positions and simply sit
 * there, which is a worse experience than no transition at all — so a browser
 * missing it is treated exactly like a player who asked for reduced motion.
 */
function canAnimate(): boolean {
  return typeof Element !== "undefined" && typeof Element.prototype.animate === "function";
}

/**
 * Runs a transition and navigates through it.
 *
 * Returns immediately; the navigation happens later, from a timer.
 */
export function transitionTo(
  to: string,
  navigate: Navigate,
  req: TransitionRequest & NavOptions & { from?: string } = {},
): void {
  const opts: NavOptions = { replace: req.replace, state: req.state };

  if (transitionsDisabled) {
    navigate(to, opts);
    return;
  }

  // A request arriving on top of a run that has already swapped means the player
  // is ahead of the animation. Abandon it and go, rather than queueing a second
  // one behind it.
  if (pending?.swapped) {
    finish();
    navigate(to, opts);
    return;
  }
  // One that arrives DURING the cover is cheaper to honour: the screen is on its
  // way to being hidden either way, so just re-point where it is going.
  if (pending) {
    pending.to = to;
    pending.navigate = navigate;
    pending.opts = opts;
    return;
  }

  const plain = reducedMotion() || !canAnimate();
  const kind = transitionKindFor(to, req);
  const style = plain ? "fade" : styleFor(kind);
  const totalMs = plain ? REDUCED_MOTION_MS : budgetFor(kind);
  const plan = transitionPlan(kind, style, totalMs);

  // Start rendering the deck's cards now: ready for this run if they can be, for
  // the next one if they cannot. Never awaited.
  if (!plain) primeCardImages();

  pending = { to, navigate, opts, swapped: false };
  const id = nextId++;
  useTransition.setState({
    run: { id, plan, seed: id * 2654435761, back: !!req.back, direction: slideFor(req.from ?? "/", to, req.back) },
    phase: "covering",
  });

  /**
   * The slide swaps the route and starts the incoming stroke in ONE callback,
   * where every other style does the two separately.
   *
   * It has to. Nothing is covering the screen, so the new route is visible the
   * instant it mounts — and if the phase change that puts it off-screen arrived
   * in a later task, there would be a frame of it sitting at rest in the middle
   * of a slide. React batches the two into one render, and the layout effect
   * that applies the stroke runs before that render is painted.
   */
  const slides = plan.style === "slide";
  timers.push(
    window.setTimeout(() => {
      if (!pending) return;
      pending.swapped = true;
      pending.navigate(pending.to, pending.opts);
      if (slides) useTransition.setState((s) => (s.run?.id === id ? { phase: "revealing" } : s));
    }, plan.coverMs),
  );
  if (!slides) {
    timers.push(
      window.setTimeout(() => {
        useTransition.setState((s) => (s.run?.id === id ? { phase: "revealing" } : s));
      }, plan.coverMs + plan.holdMs),
    );
  }
  timers.push(
    window.setTimeout(() => {
      if (useTransition.getState().run?.id === id) finish();
    }, plan.totalMs),
  );

  // The hard stop. Nothing in a healthy run can reach it.
  watchdog = window.setTimeout(() => {
    if (useTransition.getState().run?.id !== id) return;
    // Whatever went wrong, the player must not be left looking at a pack of
    // cards. Make sure the navigation happened, then pull the overlay.
    if (pending && !pending.swapped) pending.navigate(pending.to, pending.opts);
    finish();
  }, plan.totalMs * 2 + TRANSITION_WATCHDOG_SLACK_MS);
}

/** Aborts anything in flight without navigating. For teardown and tests. */
export function cancelTransition(): void {
  finish();
}
